import React, { useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { AI_ASSISTANT_PATH } from 'routes';
import { actions } from 'store/AIAssistant';
import {
  selectAIAssistantConversationById,
  selectAIAssistantConversationRequestStateById,
  selectConversationInputs,
  selectNewConversationRequestState
} from 'store/AIAssistant/selectors';
import { selectUser } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';

import AIAssistantInput from './AIAssistantInput';
import { postToConversation, startConversation } from './requests';

interface Props {
  conversationId?: number;
  setIsExitAnimationActive?: (isExitAnimationActive: boolean) => void;
  isExitAnimationActive?: boolean;
  onThreadInputFocus?: () => void;
  onThreadInputBlur?: () => void;
  autoFocus?: boolean;
  /** When true, new threads stay on the current page (e.g. Requests drawer) instead of navigating to the assistant route. */
  suppressRouteNavigation?: boolean;
  onConversationCreated?: (conversationId: number) => void;
}

const AIAssistantConversationInput: React.FC<Props> = ({
  conversationId,
  setIsExitAnimationActive,
  isExitAnimationActive,
  onThreadInputFocus,
  onThreadInputBlur,
  autoFocus,
  suppressRouteNavigation,
  onConversationCreated
}) => {
  const user = useAppSelector(selectUser);
  const tenancyId = useAppSelector(selectTenancyId);
  const conversation = useAppSelector(selectAIAssistantConversationById(conversationId || -1));
  const { isMobile } = useContext(media);
  const parsedConversationId = conversationId || -1;
  const disableAnimation = !!conversation?.ai_assistant_messages.length;
  const conversationInputs = useAppSelector(selectConversationInputs);
  const conversationInput = conversationInputs.find(i => i.conversationId === parsedConversationId);
  const currentConversationRequestState = useAppSelector(
    selectAIAssistantConversationRequestStateById(parsedConversationId)
  );
  const newConversationRequestState = useAppSelector(selectNewConversationRequestState);
  const inputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!autoFocus) return undefined;

    let cancelled = false;

    const runFocus = () => {
      if (cancelled) return;
      inputRef.current?.focus({ preventScroll: true });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(runFocus);
    });

    const timeout = window.setTimeout(runFocus, 120);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [autoFocus]);

  const setInput = (input: string) => {
    dispatch(actions.setConversationInput({ input, conversationId: parsedConversationId }));
  };

  if (!user || !tenancyId) return null;

  const handleMessage = async ({ conversationId, input }: { conversationId: number; input: string }) => {
    if (isMobile) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'instant'
      });
    } else {
      const chatContainer = document.querySelector('#ai-assistant-conversation-container');
      if (chatContainer) {
        setTimeout(() => {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }, 100);
      }
    }

    const { data: conversation } = await postToConversation({
      tenancyId,
      conversationId: conversationId,
      message: input
    });
    dispatch(actions.removePendingConversationMessage({ conversationId }));
    dispatch(actions.updateConversation({ conversation }));

    if (isMobile) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'instant'
      });
    }
  };

  const handleSubmit = async () => {
    if (
      !conversationInput ||
      !conversationInput.input ||
      conversationInput.input.trim() === '' ||
      (conversation && currentConversationRequestState === 'in_progress') ||
      newConversationRequestState === 'in_progress'
    ) {
      return;
    }

    setIsExitAnimationActive?.(true);

    const trimmedInput = conversationInput.input.trim();

    if (conversation) {
      try {
        dispatch(
          actions.setRequestStateForConversation({ conversationId: conversation.id, requestState: 'in_progress' })
        );
        dispatch(actions.addPendingConversationMessage({ conversationId: conversation.id, input: trimmedInput }));
        setInput('');
        await handleMessage({ conversationId: conversation.id, input: trimmedInput });
      } catch (error) {
        console.error(error);
        showSnackbar({ negative: true });
        dispatch(actions.setRequestStateForConversation({ conversationId: conversation.id, requestState: 'ready' }));
      } finally {
        dispatch(actions.setRequestStateForConversation({ conversationId: conversation.id, requestState: 'ready' }));
      }
    } else {
      try {
        dispatch(actions.setNewConversationRequestState('in_progress'));
        const { data } = await startConversation(tenancyId);
        dispatch(actions.setRequestStateForConversation({ conversationId: data.id, requestState: 'in_progress' }));
        dispatch(actions.addConversation(data));
        dispatch(actions.addPendingConversationMessage({ conversationId: data.id, input: trimmedInput }));
        setInput('');

        if (suppressRouteNavigation) {
          onConversationCreated?.(data.id);
          dispatch(actions.setNewConversationRequestState('ready'));
          setIsExitAnimationActive?.(false);
        }

        // Start the message processing but don't wait for it
        handleMessage({ conversationId: data.id, input: trimmedInput }).then(() => {
          // Reset request state after AI responds (even after navigation)
          dispatch(actions.setRequestStateForConversation({ conversationId: data.id, requestState: 'ready' }));
        });

        if (suppressRouteNavigation) {
          return;
        }

        // Navigate after 700ms regardless of AI response
        setTimeout(() => {
          history.push(`${AI_ASSISTANT_PATH}/${data.id}`);
          dispatch(actions.setNewConversationRequestState('ready'));
          setIsExitAnimationActive?.(false);
        }, 700);
      } catch (error) {
        console.error(error);
        showSnackbar({ negative: true });
        dispatch(actions.setNewConversationRequestState('ready'));
      }
    }
  };

  return (
    <AIAssistantInput
      isExitAnimationActive={isExitAnimationActive}
      input={conversationInput?.input || ''}
      onChange={input => setInput(input)}
      handleSubmit={handleSubmit}
      ref={inputRef}
      disableAnimation={disableAnimation}
      autoFocus={autoFocus}
      onFocus={() => {
        onThreadInputFocus?.();
      }}
      onBlur={() => {
        onThreadInputBlur?.();
      }}
    />
  );
};

export default AIAssistantConversationInput;
